import { toast } from "sonner";
import { api, formatApiErrorDetail } from "./api";

// Kick off a Pro subscription. Team self-serve remains closed until GS-035. The backend
// returns a hosted Stripe URL (Checkout for new subscribers, or the Customer
// Portal if they already have a live subscription) and we redirect there.
export async function startCheckout(plan) {
  if (plan !== "pro") {
    toast.error("This plan is not available for self-serve checkout.");
    return;
  }
  try {
    const { data } = await api.post("/billing/checkout", { plan });
    if (data?.url) {
      window.location.href = data.url;
    } else {
      toast.error("Couldn't start checkout. Please try again.");
    }
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start checkout.");
  }
}

export async function startBoosterCheckout() {
  try {
    const { data } = await api.post("/billing/booster");
    if (data?.url) window.location.href = data.url;
    else toast.error("Couldn't start booster checkout. Please try again.");
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start booster checkout.");
  }
}

// Open the Stripe-hosted Customer Portal to update payment or cancel. Configure
// its live catalog without Team until GS-035 is complete.
//
// `onNoBillingAccount` is called instead of showing an error when the account has
// no Stripe customer yet (the backend answers 400 "No billing account yet"). That
// happens for anyone entitled to a paid tier without having paid — admins, and
// comped accounts — and leaving them staring at that message was a dead end.
export async function openBillingPortal({ onNoBillingAccount } = {}) {
  try {
    const { data } = await api.post("/billing/portal");
    if (data?.url) {
      window.location.href = data.url;
    } else {
      toast.error("Couldn't open billing portal. Please try again.");
    }
  } catch (e) {
    if (e.response?.status === 400 && onNoBillingAccount) {
      onNoBillingAccount();
      return;
    }
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't open billing portal.");
  }
}

// True when the account has an actual paid subscription behind it. Distinct from
// `usage.plan`, which is the *effective* plan and can be a paid tier with no
// subscription (admins). Only a real subscriber has a Stripe portal to open.
export function hasBillingAccount(usage) {
  const billing = usage?.billing_plan ?? usage?.plan ?? "free";
  return billing !== "free";
}

// The next self-serve tier to upsell to.
export function nextTierFor(plan) {
  return plan === "free" ? "pro" : null;
}

/**
 * What the "Manage plan" / "Upgrade" button should do.
 *
 * Subscribers go to the Stripe portal, where they can switch tiers or cancel
 * back to Free. Everyone else goes to Pricing — including users already entitled
 * to a paid tier, who previously got "No billing account yet. Upgrade to a paid
 * plan first." from a button that offered no way to do that.
 */
export function managePlan(usage, navigate) {
  if (hasBillingAccount(usage)) {
    openBillingPortal({ onNoBillingAccount: () => navigate("/pricing") });
    return;
  }
  navigate("/pricing");
}

// Label matching `managePlan`'s behaviour.
export function managePlanLabel(usage) {
  if (hasBillingAccount(usage)) return "Manage plan";
  if (usage?.unlimited) return "View plans";
  return (usage?.plan ?? "free") === "free" ? "Upgrade to Pro" : "View plans";
}
